<template>
  <div class="flex flex-column gap-3">
    <div class="flex justify-content-between align-items-center">
      <h3 class="m-0">{{ table.title }}</h3>
    </div>

    <a-table
      size="small"
      :style="{ whiteSpace: 'pre' }"
      :columns="columns"
      :data-source="filteredSequences(table.data)"
      :loading="table.loading"
      :pagination="pagination"
      :rowClassName="getRowClassName"
      :rowKey="record => record.id"
      :locale="{
        emptyText: searchValue ? getEmptyText() : `No ${table.title}`
      }"
      :scroll="{ x: 1000, y: 380 }"
      :customRow="
        record => ({
          on: {
            click: () => handleSequenceReview(record.id)
          }
        })
      "
      @change="handleTableChange"
    >
      <span slot="status" slot-scope="text, record">
        <a-tag class="h-fit" :color="getCompleteRequestExecution(record.completion_status).color">
          {{ getCompleteRequestExecution(record.completion_status).title }}
        </a-tag>
      </span>

      <span slot="actions" slot-scope="text, record">
        <span :style="{ display: 'none' }">
          <export-button
            :ref="`exportRef_${record.id}`"
            :only-icon="true"
            :export-payload="exportPayload(record.id)"
            @updateUrl="url => afterDownloadDocument(url, record.id, record.type)"
        /></span>
        <button-more :record="record" :menu-items="menuItems" />
      </span>
    </a-table>
  </div>
</template>

<script>
import ExportButton from '@/components/export-button.vue'
import tableMixin from '@/mixins/tableMixin'
import util from '@/util'
import { notification, Table, Tag } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import { h } from 'vue'
import BulkArchiveMixin from '../mixins/BulkArchiveMixin'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapGetters: completeRequestGetters } = createNamespacedHelpers('completeRequest')
const { mapState: sequenceState, mapActions: sequenceActions } = createNamespacedHelpers('sequence')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-tag': Tag,
    'a-table': Table,
    'export-button': ExportButton
  },
  props: {
    searchValue: {
      type: String,
      default: undefined
    },
    table: {
      type: Object,
      default: () => {
        return {
          data: [],
          title: '',
          key: '',
          loading: false
        }
      }
    }
  },
  mixins: [tableMixin, BulkArchiveMixin],
  data() {
    return {
      columns: [
        {
          title: 'Name',
          width: 300,
          dataIndex: 'project_sequence_name',
          key: 'project_sequence_name',
          sorter: (a, b) =>
            a.project_sequence_name == null
              ? -1
              : b.project_sequence_name == null
                ? 1
                : a.project_sequence_name.localeCompare(b.project_sequence_name)
        },
        {
          title: 'Status',
          dataIndex: 'completion_status',
          key: 'completion_status',
          width: 150,
          scopedSlots: { customRender: 'status' },
          sorter: (a, b) =>
            a.completion_status == null
              ? -1
              : b.completion_status == null
                ? 1
                : a.completion_status.localeCompare(b.completion_status)
        },
        {
          title: 'Time To Complete',
          key: 'max_time',
          width: 160,
          customRender: (_, { max_time }) => {
            const days = Math.floor(max_time / (60 * 24))
            const remainingMinutesAfterDays = max_time % (60 * 24)
            const hours = Math.floor(remainingMinutesAfterDays / 60)
            const minutes = remainingMinutesAfterDays % 60

            const parts = []

            const pluralize = (value, singular, plural = `${singular}s`) => {
              return value === 1 ? `${value} ${singular}` : `${value} ${plural}`
            }

            if (days > 0) {
              parts.push(pluralize(days, 'day'))
            }
            if (hours > 0) {
              parts.push(pluralize(hours, 'hour'))
            }
            if (minutes > 0) {
              parts.push(pluralize(minutes, 'minute'))
            }

            if (parts.length === 0) {
              return '0 minutes'
            }

            return parts.join(' ')
          },
          sorter: (a, b) => a.max_time - b.max_time
        },
        {
          title: 'Updated At',
          key: 'updated_at',
          width: 150,
          customRender: (_, record) => {
            return util.convertDateTimeToCurrentTimeZoneWithFormat(record.updated_at, 'MM/DD/YYYY')
          },
          sorter: (a, b) => {
            const dateA = a.updated_at ? new Date(a.updated_at) : null
            const dateB = b.updated_at ? new Date(b.updated_at) : null

            if (!dateA && !dateB) return 0
            if (!dateA) return 1
            if (!dateB) return -1

            return dateA - dateB
          }
        },
        {
          title: 'Actions',
          width: 120,
          scopedSlots: { customRender: 'actions' }
        }
      ]
    }
  },
  computed: {
    ...completeRequestGetters(['getCompleteRequestExecution']),
    ...profileState({
      associate: state => state.associate
    }),
    ...sequenceState({
      sequences: state => state.completedAssociateSequence
    }),

    menuItems() {
      return [
        {
          key: 'open',
          icon: 'fluent:open-20-regular',
          label: 'Open',
          visible: true,
          handler: record => {
            this.handleSequenceReview(record.id)
          }
        },
        {
          key: 'archive',
          icon: 'carbon:archive',
          label: 'Delete',
          visible: true,
          handler: record => {
            this.archiveSelectedSequence(record.id, record.type)
          }
        },
        {
          key: 'archive-signed',
          icon: 'carbon:archive',
          label: 'Archive Signed Documents',
          visible: true,
          handler: record => {
            this.openBulkArchiveConfirm({
              type: 'sequence',
              object_id: record.id,
              name: record.project_sequence_name,
              onDone: () => this.loadData()
            })
          }
        },
        {
          key: 'export',
          icon: 'mdi:export',
          label: 'Export',
          visible: true,
          children: [
            {
              key: 'send-export-document',
              icon: 'carbon:email',
              label: 'Send to Email',
              visible: true,
              handler: record => {
                const refName = `exportRef_${record.id}`

                if (this.$refs[refName]) {
                  this.$refs[refName].openSendModal()
                }
              }
            },
            {
              key: 'download-export-document',
              icon: 'carbon:document-download',
              label: 'Download',
              visible: true,
              handler: record => {
                if (record.export_file) {
                  window.open(record.export_file.url, '_blank')
                } else {
                  const refName = `exportRef_${record.id}`

                  this.$refs[refName].download()
                }
              }
            }
          ]
        }
      ]
    }
  },

  methods: {
    ...profileActions(['reloadAssociate']),
    ...modalActions(['openModal']),
    ...sequenceActions(['setCompletedAssociateSequence', 'archiveSequence']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    afterDownloadDocument(url, id, sequence_type) {
      this.setCompletedAssociateSequence({
        sequence_type,
        payload: this.sequences[sequence_type].map(item => {
          if (item.id === id) {
            return { ...item, export_file: { url } }
          } else {
            return item
          }
        })
      })
    },

    filteredSequences(data) {
      if (!this.searchValue) {
        return data
      }

      const searchTerm = this.searchValue.trim().toLowerCase()

      this.pagination.current = 1

      return data.filter(
        item => item.project_sequence_name && item.project_sequence_name.toLowerCase().includes(searchTerm)
      )
    },

    getEmptyText() {
      return h('span', [
        'Nothing matched your search. Please take a look at ',
        h(
          'span',
          {
            on: {
              click: () => {
                this.$emit('click', 'company')
              }
            },
            style: { color: '#2563eb', cursor: 'pointer' }
          },
          'Documents'
        ),
        ' or at ',
        h(
          'span',
          {
            on: {
              click: () => {
                this.$emit('click', 'attached')
              }
            },
            style: { color: '#2563eb', cursor: 'pointer' }
          },
          'Attached Documents'
        ),
        ' — maybe you’ll find it there!'
      ])
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }
    },

    exportPayload(id) {
      return {
        object_ids: [id],
        type: 'sequence'
      }
    },

    handleSequenceReview(id) {
      this.openModal({
        type: 'sequenceReview',
        data: {
          visible: true,
          associate: this.associate,
          sequence_id: id,
          onSave: () => {
            this.reloadAssociate(true)
          }
        }
      })
    },

    async archiveSelectedSequence(sequence_id) {
      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Are you sure?',
          description:
            'After deleting, the document will disappear from the profile and you will only be able to restore it through ePeople support. The person will need to re-upload it. Are you sure you want to delete the document?',
          onConfirm: async () => {
            await this.archiveSequence({
              sequence_id
            })

            this.showNotification('success', 'Success', 'Successfully deleted the onboarding.')

            this.reloadAssociate(true)
          }
        }
      })
    }
  }
}
</script>

<style scoped>
::v-deep .ant-table-row-cell-break-word {
  text-wrap: initial;
}
</style>
